import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Upload, Trash2, Download, FolderDown, Share2, Eye,
  ImageIcon, Video as VideoIcon, Star, Pencil, Code2, Copy, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { storySetsAPI } from '../lib/api';
import { chunkedUpload } from '../lib/chunkedUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StorySetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [set, setSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState({ name: '', percent: 0, index: 0, total: 0 });
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [copiedApi, setCopiedApi] = useState(false);
  const [zipping, setZipping] = useState(false);

  const load = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true);
      const res = await storySetsAPI.getSet(id);
      setSet(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load set');
    } finally { if (!silent) setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadInfo({ name: f.name, percent: 0, index: i + 1, total: files.length });
        await chunkedUpload(f, 'story_set_media', id, (p) => setUploadInfo((u) => ({ ...u, percent: p })));
      }
      toast.success(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'} at full quality`);
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadInfo({ name: '', percent: 0, index: 0, total: 0 });
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveName = async () => {
    const n = nameDraft.trim();
    setEditingName(false);
    if (!n || n === set.name) return;
    try {
      const res = await storySetsAPI.renameSet(id, n);
      setSet((s) => ({ ...s, name: res.data.name }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const onMediaDelete = (mid) => setSet((s) => ({ ...s, media: (s.media || []).filter((m) => m.id !== mid) }));

  const handleDownloadZip = async () => {
    try {
      setZipping(true);
      const res = await storySetsAPI.downloadZip(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(set.name || 'story-set').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      const skipped = Number(res.headers['x-files-skipped'] || 0);
      toast.success(
        skipped
          ? `Downloaded ${res.headers['x-files-included']} files — ${skipped} too large, use their own Download button`
          : `Downloaded ${res.headers['x-files-included'] || (set.media || []).length} files at full quality`,
      );
    } catch (err) {
      const detail = err.response?.data instanceof Blob
        ? 'Download failed' : err.response?.data?.detail;
      toast.error(detail || 'Download failed');
    } finally { setZipping(false); }
  };

  const agentUrl = `${BACKEND_URL}/api/story-sets/agent/sets/${id}`;
  const copyAgentUrl = () => {
    navigator.clipboard.writeText(agentUrl);
    setCopiedApi(true);
    setTimeout(() => setCopiedApi(false), 1400);
    toast.success('Agent API URL copied');
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!set) return null;

  const media = set.media || [];

  return (
    <div className="max-w-6xl mx-auto" data-testid="story-set-detail">
      <button onClick={() => navigate('/story-sets')} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="back-to-sets">
        <ArrowLeft className="w-4 h-4" /> All sets
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="min-w-0">
          {editingName ? (
            <Input
              autoFocus value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
              className="text-xl font-semibold h-10 max-w-md"
              data-testid="set-name-edit-input"
            />
          ) : (
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 inline-flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {set.name}
              <button onClick={() => { setNameDraft(set.name); setEditingName(true); }} className="text-zinc-300 hover:text-rose-600" data-testid="edit-set-name-btn"><Pencil className="w-4 h-4" /></button>
            </h1>
          )}
          <p className="text-sm text-zinc-500 mt-1 truncate">Product: {set.listing_title}</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={handleFiles} className="hidden" data-testid="story-media-file-input" />
          <Button
            onClick={handleDownloadZip}
            disabled={zipping || media.length === 0}
            variant="outline"
            className="border-zinc-300"
            data-testid="download-all-btn"
            title="Download every file in this set as one ZIP, full original quality"
          >
            {zipping ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FolderDown className="w-4 h-4 mr-1.5" />}
            {zipping ? 'Zipping…' : `Download all${media.length ? ` (${media.length})` : ''}`}
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="add-media-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Add media
          </Button>
        </div>
      </div>

      {/* Agent read API */}
      <div className="mb-6 bg-zinc-900 rounded-xl p-4 flex items-start gap-3" data-testid="agent-api-card">
        <div className="shrink-0 w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-rose-400"><Code2 className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-zinc-200">Agent read API <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-zinc-700 text-zinc-300 rounded px-1.5 py-0.5">READ-ONLY · GET</span></p>
          <p className="text-[11px] text-zinc-400 mt-1">Returns this set's media with direct full-quality <code className="text-zinc-300">download_url</code> + <code className="text-zinc-300">public_url</code> for AI agents to post to Instagram. Authenticate with header <code className="text-rose-300">X-API-Key: &lt;your CRM API key&gt;</code> (recommended for agents) — or a Bearer login token.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 text-[11px] text-emerald-300 bg-black/40 rounded-md px-2.5 py-1.5 font-mono truncate" data-testid="agent-api-url">{agentUrl}</code>
            <button onClick={copyAgentUrl} className="shrink-0 text-zinc-400 hover:text-white p-1.5" data-testid="copy-agent-api-btn">
              {copiedApi ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {uploading && (
        <div className="mb-5 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm text-zinc-700 flex items-center gap-3" data-testid="story-upload-progress">
          <Loader2 className="w-4 h-4 animate-spin text-rose-600" />
          <span className="truncate flex-1">Uploading {uploadInfo.index}/{uploadInfo.total} · {uploadInfo.name}</span>
          <span className="font-mono text-xs">{uploadInfo.percent}%</span>
        </div>
      )}

      {media.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-2xl py-20 text-center" data-testid="story-media-empty">
          <Upload className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-600 font-medium">Add images & videos to this set</p>
          <p className="text-zinc-400 text-sm mt-1">Uploaded at full quality. The first image becomes the set thumbnail.</p>
          <Button onClick={() => fileRef.current?.click()} className="mt-5 bg-rose-600 hover:bg-rose-700 text-white" data-testid="add-media-empty-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Add media
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="story-media-grid">
          {media.map((m) => (
            <MediaCard
              key={m.id} setId={id} media={m}
              isThumb={m.r2_key === set.thumbnail_r2_key}
              onDelete={onMediaDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaCard({ setId, media, isThumb, onDelete }) {
  const [saving, setSaving] = useState(false);

  const fallbackDownload = (blob, fname) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // On iPhone this opens the native share sheet → "Save Image" / Save to Photos.
  // On desktop/Android it falls back to a normal full-quality download.
  const handleSave = async () => {
    const fname = media.seo_filename || `${media.seo_base || 'thelocaljewel-story'}${media.ext || ''}`;
    try {
      setSaving(true);
      const res = await storySetsAPI.getMediaFileBlob(setId, media.id);
      const blob = res.data;
      const type = blob.type || media.content_type || 'application/octet-stream';
      const file = new File([blob], fname, { type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: fname });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return; // user dismissed the sheet
        }
      }
      fallbackDownload(blob, fname);
    } catch (err) {
      // Large videos (>60MB) aren't proxied — use the presigned download link.
      if (media.download_url) {
        window.location.href = media.download_url;
      } else {
        toast.error('Could not prepare the file. Try the share link.');
      }
    } finally { setSaving(false); }
  };

  const handleShare = () => {
    const link = media.share_url || media.public_url || media.view_url;
    if (link) { navigator.clipboard.writeText(link); toast.success('Share link copied'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this media from the set?')) return;
    try {
      await storySetsAPI.deleteMedia(setId, media.id);
      onDelete(media.id);
      toast.success('Removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const isVideo = media.kind === 'video';

  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden group" data-testid={`story-media-card-${media.id}`}>
      <div className="relative bg-zinc-900 aspect-[4/5] flex items-center justify-center overflow-hidden">
        {isVideo ? (
          <video src={media.view_url} controls className="w-full h-full object-contain" data-testid={`story-media-video-${media.id}`} />
        ) : (
          <img src={media.view_url} alt="story media" className="w-full h-full object-cover" data-testid={`story-media-image-${media.id}`} />
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-medium bg-black/60 text-white rounded-full px-2 py-0.5">
          {isVideo ? <VideoIcon className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />} {media.kind}
        </span>
        {isThumb && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-medium bg-rose-600 text-white rounded-full px-2 py-0.5" data-testid={`thumb-badge-${media.id}`}>
            <Star className="w-3 h-3" /> Cover
          </span>
        )}
        <button onClick={handleDelete} className="absolute bottom-2 right-2 p-1.5 bg-white/90 rounded-md text-zinc-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-media-${media.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2.5 space-y-1.5">
        <Button
          size="sm" onClick={handleSave} disabled={saving}
          className="w-full h-8 bg-rose-600 hover:bg-rose-700 text-white"
          data-testid={`save-photos-btn-${media.id}`}
          title={isVideo ? 'Save video to your device' : 'Save to Photos (iPhone) or download'}
        >
          {saving
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            : <Download className="w-3.5 h-3.5 mr-1.5" />}
          {isVideo ? 'Save video' : 'Save to Photos'}
        </Button>
        <a
          href={media.download_url || media.view_url}
          download={media.seo_filename || undefined}
          target="_blank"
          rel="noreferrer"
          className="block"
          data-testid={`download-btn-${media.id}`}
          title="Download the original file to this device"
        >
          <Button size="sm" variant="outline" className="w-full h-7 border-zinc-300">
            <Download className="w-3.5 h-3.5 mr-1.5" /><span className="text-xs">Download file</span>
          </Button>
        </a>
        <div className="flex items-center gap-1.5">
          <a href={media.view_url} target="_blank" rel="noreferrer" className="flex-1">
            <Button size="sm" variant="outline" className="w-full h-7 px-0" data-testid={`view-btn-${media.id}`}><Eye className="w-3.5 h-3.5 mr-1" /><span className="text-xs">View</span></Button>
          </a>
          <Button size="sm" variant="outline" onClick={handleShare} className="flex-1 h-7 px-0" data-testid={`share-btn-${media.id}`}><Share2 className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Link</span></Button>
        </div>
      </div>
    </div>
  );
}
